import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import { api } from '../../api/client';
import { ReactComponent as Logo } from '../../logo.svg';
import { isNewUiUser } from '../access';
import { v2Path } from '../nav';
import { CheckIcon } from '../components/icons';
import { cx, CARD_SURFACE, FOCUS_RING } from '../components/styles';
import '../newui.css';

/**
 * v2 replacement for `src/pages/LoginPage.tsx`.
 *
 * Zero functionality change, per DESIGN_PLAN §0: the state machine, the three
 * modes, the validation strings, the Firebase error map, the `useAuth` calls,
 * the toasts, the `trackEvent` names and the `?error=access_disabled` cleanup
 * are all v1's, verbatim. Only the layout and the class strings are new.
 *
 * Self-contained on purpose — it mounts at `/login`, outside `NewUiEntry`, so it
 * carries its own `data-v2-root` (which is what scopes `newui.css`, including
 * the reduced-motion block) and imports the stylesheet itself.
 */

/**
 * `reset` splits into three steps: ask for a code, enter it, then set the
 * password. They are modes rather than a nested state machine so the existing
 * per-mode copy, fade animation and validation keep working unchanged.
 */
type AuthMode = 'login' | 'signup' | 'reset' | 'reset-code' | 'reset-password';

/**
 * Where a freshly authenticated account lands.
 *
 * Allowlisted accounts go straight into the /v2 revamp so it is the surface they
 * actually live in rather than one they have to remember to type. `isNewUiUser`
 * normalises the address, and it is the same predicate `AllowlistGate` uses, so
 * the two can never disagree and bounce the user between surfaces. Every other
 * account keeps v1's destination exactly.
 */
function postAuthRoute(email: string): string {
  return isNewUiUser(email) ? v2Path('/notifications') : '/notifications';
}

/* ------------------------------------------------------------------ *
 * Validation — v1's rules and v1's exact strings, factored into three
 * pure helpers so blur-time and submit-time validation cannot drift.
 * ------------------------------------------------------------------ */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emailError(email: string): string | undefined {
  if (!email) return 'Email is required';
  if (!EMAIL_PATTERN.test(email)) return 'Please enter a valid email';
  return undefined;
}

function passwordError(password: string): string | undefined {
  if (!password) return 'Password is required';
  if (password.length < 6) return 'Password must be at least 6 characters';
  return undefined;
}

/**
 * Stricter than `passwordError`, and mirrors password_error() in
 * flashtools/services/password_reset.py. Kept separate so signup's existing
 * 6-character rule is untouched. The server rule is the one that counts;
 * this only spares the user a round trip.
 */
function resetPasswordError(password: string): string | undefined {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (/^\d+$/.test(password) || /^[a-zA-Z]+$/.test(password)) {
    return 'Password must contain both letters and numbers';
  }
  return undefined;
}

function codeError(code: string): string | undefined {
  if (!code) return 'Enter the 6-digit code';
  if (!/^\d{6}$/.test(code)) return 'The code is 6 digits';
  return undefined;
}

function confirmPasswordError(password: string, confirmPassword: string): string | undefined {
  if (!confirmPassword) return 'Please confirm your password';
  if (password !== confirmPassword) return 'Passwords do not match';
  return undefined;
}

/* ------------------------------------------------------------------ *
 * Per-mode copy. One h1 per mode; the left brand panel deliberately
 * uses <p>, not a heading, so the form heading is the page's only h1.
 * ------------------------------------------------------------------ */

const MODE_COPY: Record<AuthMode, { heading: string; subcopy: string; submit: string; pending: string }> = {
  login: {
    heading: 'Welcome back',
    subcopy: 'Sign in to run every ticket account you own.',
    submit: 'Sign In',
    pending: 'Signing in...',
  },
  signup: {
    heading: 'Create your account',
    subcopy: 'Connect DICE, AXS and TAO once. Then work from one dashboard.',
    submit: 'Create Account',
    pending: 'Creating account...',
  },
  reset: {
    heading: 'Reset your password',
    subcopy: "Enter your email and we'll send you a 6-digit code.",
    submit: 'Send Code',
    pending: 'Sending...',
  },
  'reset-code': {
    heading: 'Enter your code',
    subcopy: 'Check your inbox for the 6-digit code. It expires in 15 minutes.',
    submit: 'Verify Code',
    pending: 'Verifying...',
  },
  'reset-password': {
    heading: 'Set a new password',
    subcopy: 'Choose a password you have not used before.',
    submit: 'Update Password',
    pending: 'Updating...',
  },
};

/** Static outcome lines, reused conceptually from the landing's OutcomeRow. */
const BRAND_POINTS = [
  'Alerts when your venues go on sale',
  'Join waitlists across every account at once',
  'One price change, everywhere it is listed',
];

/* ------------------------------------------------------------------ *
 * Icons — inline stroke SVGs matching components/icons.tsx.
 * ------------------------------------------------------------------ */

const strokeBase = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: 'false' as const,
};

const EyeIcon: React.FC<{ className?: string }> = ({ className = 'w-[18px] h-[18px]' }) => (
  <svg {...strokeBase} className={className}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <path d="M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
  </svg>
);

const EyeOffIcon: React.FC<{ className?: string }> = ({ className = 'w-[18px] h-[18px]' }) => (
  <svg {...strokeBase} className={className}>
    <path d="M4 4l16 16" />
    <path d="M9.9 5.8A9.9 9.9 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17.4 17.4 0 0 1-3.4 4.2" />
    <path d="M6.6 7.9A17.2 17.2 0 0 0 2.5 12S6 18.5 12 18.5c1 0 1.9-.1 2.7-.4" />
    <path d="M10.2 10.2a2.5 2.5 0 0 0 3.5 3.5" />
  </svg>
);

const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg {...strokeBase} className={className}>
    <path d="M20 12H5" />
    <path d="M10 7l-5 5 5 5" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Field
 * ------------------------------------------------------------------ */

interface FieldProps {
  id: string;
  label: string;
  type: 'email' | 'password' | 'text';
  value: string;
  autoComplete: string;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric';
  maxLength?: number;
  inputClassName?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

const Field: React.FC<FieldProps> = ({
  id,
  label,
  type,
  value,
  autoComplete,
  placeholder,
  error,
  disabled,
  inputMode,
  maxLength,
  inputClassName,
  onChange,
  onBlur,
}) => {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const errorId = `${id}-error`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white/70">
        {label}
      </label>
      <div className="relative mt-1.5">
        <input
          id={id}
          name={id}
          type={isPassword && revealed ? 'text' : type}
          inputMode={inputMode ?? (type === 'email' ? 'email' : undefined)}
          maxLength={maxLength}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={cx(
            'h-11 w-full rounded-lg border bg-white/[0.03] px-3.5 text-[15px] text-white',
            'placeholder:text-white/30 transition-colors duration-150',
            'disabled:cursor-not-allowed disabled:opacity-60',
            isPassword && 'pr-12',
            error
              ? 'border-red-400/60 focus:border-red-400/60'
              : 'border-white/[0.10] hover:border-white/[0.18] focus:border-[#3b82f6]/60',
            inputClassName,
            FOCUS_RING,
          )}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className={cx(
              'absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-lg',
              'text-white/40 transition-colors duration-150 hover:text-white/80',
              FOCUS_RING,
            )}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export const AuthScreen: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  /** Single-use token from the verify step; the only thing that can set a password. */
  const [resetToken, setResetToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    code?: string;
  }>({});
  /** Display-only: the reset email that was just sent, shown once we bounce back to login. */
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  const { login, signup } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Seat invites link straight to the code step with the address prefilled.
  //
  // Without this the button in the invite email lands on a bare login form,
  // and the only route to the code field is "Forgot password?" — which issues
  // a NEW code and retires the one that was just emailed. The code in the
  // invite would be decorative, and the chair would wonder why it never works.
  //
  // Only `reset-code` and `signup` are honoured. Accepting an arbitrary mode
  // from the query string would let a link drop someone straight into
  // `reset-password`, which is a step that should only ever be reached by
  // proving you hold a code.
  //
  // `signup` exists for the landing page's "Start free trial" buttons. Without
  // it every one of them landed on "Welcome back" — a sign-in form — and a new
  // visitor had to notice the small "Sign up" link underneath to get an
  // account at all. On a phone, where the nav's "Sign in" was also hidden for a
  // while, that read as there being no sign-up (and no sign-in) at all.
  useEffect(() => {
    const requested = searchParams.get('mode');
    if (requested === 'signup') {
      setMode('signup');
      return;
    }
    if (requested !== 'reset-code') return;
    const invited = searchParams.get('email');
    if (invited) setEmail(invited);
    setMode('reset-code');
  }, [searchParams]);

  // Check for access disabled error on mount
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'access_disabled') {
      showError('Your Flashtools access has been disabled. Please contact support.');
      // Clean up URL
      window.history.replaceState({}, '', '/login');
      // Ensure flag is cleared
      localStorage.removeItem('flashtools_access_disabled');
    } else if (error === 'session_expired') {
      // An admin impersonation session whose tokens could not be renewed.
      // The password form below is not the way back in — the admin panel is.
      showError('Your admin session has expired. Launch it again from the admin panel.');
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams, showError]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // The email field is only on screen for the first three modes; the later
    // reset steps carry it in state and must not re-validate a hidden input.
    if (mode === 'login' || mode === 'signup' || mode === 'reset') {
      const emailMessage = emailError(email);
      if (emailMessage) {
        newErrors.email = emailMessage;
      }
    }

    if (mode === 'login' || mode === 'signup') {
      const passwordMessage = passwordError(password);
      if (passwordMessage) {
        newErrors.password = passwordMessage;
      }

      if (mode === 'signup') {
        const confirmMessage = confirmPasswordError(password, confirmPassword);
        if (confirmMessage) {
          newErrors.confirmPassword = confirmMessage;
        }
      }
    }

    if (mode === 'reset-code') {
      const message = codeError(resetCode);
      if (message) {
        newErrors.code = message;
      }
    }

    if (mode === 'reset-password') {
      const passwordMessage = resetPasswordError(password);
      if (passwordMessage) {
        newErrors.password = passwordMessage;
      }
      const confirmMessage = confirmPasswordError(password, confirmPassword);
      if (confirmMessage) {
        newErrors.confirmPassword = confirmMessage;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFirebaseErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/invalid-credential':
        return 'Invalid email or password';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/weak-password':
        return 'Password is too weak';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later';
      default:
        return 'An error occurred. Please try again';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        // Clear any access disabled flag before login
        localStorage.removeItem('flashtools_access_disabled');
        await login(email, password);
        showSuccess('Welcome back!');
        trackEvent('user_logged_in');
        navigate(postAuthRoute(email));
      } else if (mode === 'signup') {
        // Clear any access disabled flag before signup
        localStorage.removeItem('flashtools_access_disabled');
        await signup(email, password);
        showSuccess('Account created successfully!');
        trackEvent('user_signed_up');
        navigate(postAuthRoute(email));
      } else if (mode === 'reset') {
        // Always resolves, even for an address with no account — the server
        // answers identically either way. Do not branch on the result.
        await api.requestPasswordReset(email);
        trackEvent('password_reset_requested');
        setResetSentTo(email);
        setResetCode('');
        setMode('reset-code');
      } else if (mode === 'reset-code') {
        const { reset_token } = await api.verifyPasswordResetCode(email, resetCode);
        trackEvent('password_reset_code_verified');
        setResetToken(reset_token);
        setPassword('');
        setConfirmPassword('');
        setMode('reset-password');
      } else {
        await api.confirmPasswordReset(resetToken, password, confirmPassword);
        trackEvent('password_reset_completed');
        // Sign in with what they just set, so a successful reset lands in the
        // app instead of dumping them back on the login form.
        await login(email, password);
        showSuccess('Password updated. Welcome back!');
        setResetToken('');
        setResetSentTo(null);
        navigate(postAuthRoute(email));
      }
    } catch (error: any) {
      const errorCode = error?.code || '';
      trackEvent('auth_error', { mode, errorCode });
      // Reset steps surface the server's own message; Firebase calls carry a
      // code that maps to friendlier copy.
      const isResetStep = mode === 'reset' || mode === 'reset-code' || mode === 'reset-password';
      showError(
        isResetStep && !errorCode
          ? error?.message || 'Something went wrong. Please try again.'
          : getFirebaseErrorMessage(errorCode),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setErrors({});
    // Leaving the chain at any point discards the code and the token, so a
    // half-finished reset can never be resumed from a stale token.
    setResetSentTo(null);
    setResetCode('');
    setResetToken('');
    if (newMode === 'reset') {
      setPassword('');
      setConfirmPassword('');
    }
  };

  /** Blur-time validation. Same helpers as `validateForm`, one field at a time. */
  const blurEmail = () => setErrors((prev) => ({ ...prev, email: emailError(email) }));
  const blurPassword = () =>
    setErrors((prev) => ({
      ...prev,
      password: mode === 'reset-password' ? resetPasswordError(password) : passwordError(password),
    }));
  const blurCode = () => setErrors((prev) => ({ ...prev, code: codeError(resetCode) }));
  const blurConfirm = () =>
    setErrors((prev) => ({ ...prev, confirmPassword: confirmPasswordError(password, confirmPassword) }));

  const copy = MODE_COPY[mode];
  const headingId = 'auth-heading';

  const switchLink = cx(
    'rounded px-1 py-2 font-medium text-[#3b82f6] transition-colors duration-150 hover:text-[#2563eb]',
    FOCUS_RING,
  );

  const modeSwitcher = (
    <div className="text-center text-sm">
      {mode === 'login' && (
        <>
          <span className="text-white/50">Don't have an account? </span>
          <button type="button" onClick={() => switchMode('signup')} className={switchLink}>
            Sign up
          </button>
        </>
      )}
      {mode === 'signup' && (
        <>
          <span className="text-white/50">Already have an account? </span>
          <button type="button" onClick={() => switchMode('login')} className={switchLink}>
            Sign in
          </button>
        </>
      )}
      {(mode === 'reset' || mode === 'reset-password') && (
        <button type="button" onClick={() => switchMode('login')} className={switchLink}>
          Back to sign in
        </button>
      )}
      {mode === 'reset-code' && (
        <>
          <span className="text-white/50">Didn't get it? </span>
          <button type="button" onClick={() => switchMode('reset')} className={switchLink}>
            Send a new code
          </button>
        </>
      )}
    </div>
  );

  return (
    <div data-v2-root className="min-h-screen bg-[#0a0a0a] text-white lg:grid lg:grid-cols-2">
      {/* ---------------- Brand panel (lg+ only) ---------------- */}
      <aside className="relative hidden overflow-hidden border-r border-white/[0.06] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 55% at 25% 20%, rgba(59,130,246,0.16), rgba(10,10,10,0) 72%)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(75% 60% at 30% 30%, #000 0%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(75% 60% at 30% 30%, #000 0%, transparent 75%)',
          }}
        />

        <Link
          to="/"
          className={cx('relative flex w-fit items-center gap-2.5 rounded-lg', FOCUS_RING)}
        >
          <Logo style={{ width: '32px', height: '32px' }} />
          <span className="text-lg font-bold tracking-tight text-[#3b82f6]">FlashTools</span>
        </Link>

        <div className="relative max-w-[440px]">
          <p className="text-xs uppercase tracking-[0.16em] text-[#60a5fa]">FOR TICKET SELLERS</p>
          <p className="mt-4 text-[34px] font-bold leading-[1.1] tracking-tight text-white">
            One place to run every ticket account you own.
          </p>
          <ul className="mt-8 space-y-3.5">
            {BRAND_POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-[#3b82f6]/25 bg-[#3b82f6]/10 text-[#60a5fa]">
                  <CheckIcon className="h-3 w-3" />
                </span>
                <span className="text-sm leading-relaxed text-white/60">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-white/30">
          Works with DICE &middot; AXS &middot; TAO
        </p>
      </aside>

      {/* ---------------- Form panel ---------------- */}
      <main className="flex min-h-screen flex-col px-6 py-6 sm:px-8 lg:min-h-0 lg:py-8">
        <div className="mx-auto w-full max-w-[400px]">
          <Link
            to="/"
            className={cx(
              'inline-flex min-h-[44px] items-center gap-2 rounded-lg pr-2 text-sm text-white/50',
              'transition-colors duration-150 hover:text-white/80',
              FOCUS_RING,
            )}
          >
            <ArrowLeftIcon />
            Back
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center py-6">
          <div className="w-full max-w-[400px]">
            {/* Mobile brand lockup — the aside is hidden below lg. */}
            <div className="mb-8 flex flex-col items-center gap-3 lg:hidden">
              <Logo style={{ width: '56px', height: '56px' }} />
              <span className="text-xl font-bold tracking-tight text-[#3b82f6]">FlashTools</span>
            </div>

            <div className={cx(CARD_SURFACE, 'p-6 sm:p-8')}>
              {/* key={mode} restarts the 150ms opacity fade on every mode change. */}
              <div key={mode} className="v2-auth-fade">
                <h1 id={headingId} className="text-2xl font-bold tracking-tight text-white">
                  {copy.heading}
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{copy.subcopy}</p>

                {mode === 'reset-code' && resetSentTo && (
                  <p className="mt-4 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] px-3.5 py-3 text-sm text-emerald-300">
                    If an account exists for {resetSentTo}, a code is on its way.
                  </p>
                )}

                <form
                  onSubmit={handleSubmit}
                  aria-busy={isSubmitting}
                  aria-labelledby={headingId}
                  noValidate
                  className="mt-6 space-y-4"
                >
                  {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
                  <Field
                    id="email"
                    label="Email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    placeholder="you@example.com"
                    error={errors.email}
                    disabled={isSubmitting}
                    onChange={(v) => {
                      setEmail(v);
                      if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    onBlur={blurEmail}
                  />
                  )}

                  {mode === 'reset-code' && (
                    <Field
                      id="resetCode"
                      label="6-digit code"
                      type="text"
                      value={resetCode}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      inputClassName="text-center text-lg font-semibold tracking-[0.4em]"
                      error={errors.code}
                      disabled={isSubmitting}
                      onChange={(v) => {
                        // Strip anything non-numeric so a pasted code with spaces
                        // or a stray dash still validates.
                        setResetCode(v.replace(/\D/g, '').slice(0, 6));
                        if (errors.code) setErrors((prev) => ({ ...prev, code: undefined }));
                      }}
                      onBlur={blurCode}
                    />
                  )}

                  {(mode === 'login' || mode === 'signup' || mode === 'reset-password') && (
                    <Field
                      id="password"
                      label={mode === 'reset-password' ? 'New password' : 'Password'}
                      type="password"
                      value={password}
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      placeholder={
                        mode === 'login'
                          ? 'Enter your password'
                          : mode === 'reset-password'
                            ? 'At least 8 characters, letters and numbers'
                            : 'At least 6 characters'
                      }
                      error={errors.password}
                      disabled={isSubmitting}
                      onChange={(v) => {
                        setPassword(v);
                        if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                      }}
                      onBlur={blurPassword}
                    />
                  )}

                  {(mode === 'signup' || mode === 'reset-password') && (
                    <Field
                      id="confirmPassword"
                      label={mode === 'reset-password' ? 'Confirm new password' : 'Confirm Password'}
                      type="password"
                      value={confirmPassword}
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                      error={errors.confirmPassword}
                      disabled={isSubmitting}
                      onChange={(v) => {
                        setConfirmPassword(v);
                        if (errors.confirmPassword) {
                          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                        }
                      }}
                      onBlur={blurConfirm}
                    />
                  )}

                  {mode === 'login' && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => switchMode('reset')}
                        className={cx(
                          'inline-flex min-h-[44px] items-center rounded px-1 text-sm text-[#3b82f6]',
                          'transition-colors duration-150 hover:text-[#2563eb]',
                          FOCUS_RING,
                        )}
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cx(
                      'flex h-11 w-full items-center justify-center gap-2 rounded-lg',
                      'bg-[#3b82f6] text-sm font-semibold text-white',
                      'transition-colors duration-150 hover:bg-[#2563eb]',
                      'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#3b82f6]',
                      FOCUS_RING,
                    )}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
                        />
                        {copy.pending}
                      </>
                    ) : (
                      copy.submit
                    )}
                  </button>

                  {mode === 'signup' && (
                    <p className="text-center text-xs leading-relaxed text-white/40">
                      By creating an account you agree to our{' '}
                      <Link
                        to="/terms"
                        className={cx('rounded text-white/60 underline underline-offset-2 hover:text-white', FOCUS_RING)}
                      >
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy"
                        className={cx('rounded text-white/60 underline underline-offset-2 hover:text-white', FOCUS_RING)}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  )}
                </form>
              </div>

              <div className="mt-6 border-t border-white/[0.06] pt-5">{modeSwitcher}</div>
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[400px] items-center justify-center gap-2 text-xs text-white/30">
          <Link
            to="/privacy"
            className={cx(
              'inline-flex min-h-[40px] items-center rounded px-1 transition-colors duration-150 hover:text-white/60',
              FOCUS_RING,
            )}
          >
            Privacy
          </Link>
          <span aria-hidden="true">&middot;</span>
          <Link
            to="/terms"
            className={cx(
              'inline-flex min-h-[40px] items-center rounded px-1 transition-colors duration-150 hover:text-white/60',
              FOCUS_RING,
            )}
          >
            Terms
          </Link>
        </div>
      </main>
    </div>
  );
};
